import { Box, CircularProgress } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { useParams, defer, redirect } from 'react-router-dom'
import { ArticleViewLoading } from '../../components'
import { errorSlice } from '../../reducers'
import NotFound from '../not-found'
import { ToolkitPrivateRoute } from './../../components/toolkit-private-route'
import { getAllUrlParameters } from '../../helpers/getAllUrlParameters'
import Cookies from 'universal-cookie'

const ContentPage = React.lazy(() => import('./../content'))
const CategoryLandingPage = React.lazy(() => import('./../category-landing'))

const RoutingLazyLoading = () => (
  <Box display='flex' flex={1} justifyContent='center' alignContent='center' p={10}>
    <CircularProgress size={75} />
  </Box>
)

const Location = () => {
  const { locationId } = useParams()
  const error = useSelector(errorSlice.selectors.selectError)

  if (error?.statusCode) {
    return <NotFound />
  }

  switch (locationId[0]) {
    case 'a':
      return (
        <React.Suspense fallback={<ArticleViewLoading />}>
          <ContentPage />
        </React.Suspense>
      )
    case 'c':
      return (
        <React.Suspense fallback={<RoutingLazyLoading />}>
          <CategoryLandingPage />
        </React.Suspense>
      )
    default:
      return <NotFound />
  }
}

const Page = () => {
  return (
    <ToolkitPrivateRoute publicOnlyForPublicTenant element={<Location />} />
  )
}

const loadArticleFromClient = async (locationId) => {
  return {
    loadClient: true
  }
}

const loadArticleFromCdn = async (locationId, lang = 'en') => {
  const cdnUrl = `${window.location.origin}/cdn/content/${locationId}/${lang}`
  try {
    const response = await fetch(cdnUrl, {
      credentials: 'same-origin'
    })
    const data = await response.json()
    return { content: data, translated: lang !== 'en', lang }
  } catch (error) {
    console.error('Error loading article from CDN', error)
    return loadArticleFromClient(locationId)
  }
}

const loader = async ({ params, request }) => {
  const { locationId } = params
  const friendlyUrl = params['*']
  if (!locationId || locationId[0] !== 'a') {
    return defer({
      contentPromise: loadArticleFromClient(locationId)
    })
  }

  const { url } = request
  const urlParams = getAllUrlParameters(url)

  const isLocal = url.match(/localhost/)

  const isPlaylist = url.includes('playlist') && urlParams.playlistid
  const playlistId = urlParams.playlistid
  const defaultLanguage = localStorage.getItem('defaultLanguage')

  if (!urlParams.lang && defaultLanguage && defaultLanguage !== 'en') {
    const baseUrl = `/${locationId}/${friendlyUrl}`
    const queryParams = new URLSearchParams(urlParams)
    if (isPlaylist) {
      return redirect(`${baseUrl}?playlistId=${playlistId}&lang=${defaultLanguage}`)
    }
    return redirect(`${baseUrl}?${queryParams.toString()}&lang=${defaultLanguage}`)
  }

  if (isLocal) {
    return defer({
      contentPromise: loadArticleFromClient(locationId)
    })
  }

  const cookies = new Cookies()

  const policy = cookies.get('CloudFront-Policy')
  const signature = cookies.get('CloudFront-Signature')
  const keyPairId = cookies.get('CloudFront-Key-Pair-Id')

  if (!policy || !signature || !keyPairId) {
    return defer({
      contentPromise: loadArticleFromClient(locationId)
    })
  }

  return defer({
    contentPromise: loadArticleFromCdn(locationId, urlParams.lang)
  })
}

export {
  Page as Component,
  loader
}
